/**
 * Format a numeric value as a currency string.
 *
 * This function takes a numeric value and formats it as a currency string. It handles cases where the value is zero,
 * very close to zero, or larger than 10. For values less than 10, it includes two decimal places.
 *
 * @param {number} value - The numeric value to be formatted as currency.
 * @param {number} [decimalPlaces] - Optional fixed decimal precision override.
 * @returns {string} The formatted currency string.
 */
export function formatCurrencyValue(
    value: number,
    decimalPlaces?: number,
): string {
    // Check if the input value is not a number, return an empty string
    if (typeof value !== "number" || !isFinite(value)) {
        return "";
    }

    // Get the absolute value of the number
    const abs = Math.abs(value);

    if (decimalPlaces !== undefined) {
        const normalizedDecimalPlaces = Math.max(0, Math.floor(decimalPlaces));
        if (abs === 0) {
            return (0).toFixed(normalizedDecimalPlaces);
        }

        if (normalizedDecimalPlaces > 0) {
            const threshold = 1 / 10 ** normalizedDecimalPlaces;

            if (abs < threshold) {
                return `< ${threshold.toFixed(normalizedDecimalPlaces)}`;
            }
        }

        return value.toLocaleString("en-US", {
            minimumFractionDigits: normalizedDecimalPlaces,
            maximumFractionDigits: normalizedDecimalPlaces,
        });
    }

    // 1️⃣ Near-zero noise threshold
    const threshold = 0.001;
    if (abs < threshold) {
        return `< ${threshold}`;
    }

    // 2️⃣ Very small values (but above threshold)
    // Show significant digits instead of forcing 2 decimals
    if (abs < 0.01) {
        return Number(value.toPrecision(4)).toString();
    }

    // 3️⃣ Standard small currency formatting for values less than 100
    if (abs < 100) {
        return value.toFixed(2);
    }

    // If none of the above conditions match, round the value and format it with a comma separator for thousands
    return Math.round(value).toLocaleString();
}
